import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Home, Linkedin, Briefcase, Users, BarChart3, Palette, FileText, TrendingUp, FlaskConical, ArrowRight, ChevronRight, Trees, MapPin } from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker, Line, useMapContext } from 'react-simple-maps';
import { Link } from 'react-router-dom';
import Typed from 'typed.js';
import useSound from 'use-sound';
import { useTheme } from './context/ThemeContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const MAX_RESPONSES = 5;

const SUGGESTIONS = [
  "What's your experience at Skillz?",
  "Tell me about your PM journey",
  "What projects have you built?",
  "What makes you stand out as a PM?",
];

const CONTEXTUAL_FOLLOW_UPS: { keywords: string[]; suggestions: string[] }[] = [
  {
    keywords: ['store', 'deposit', 'store revamp', 'ux overhaul'],
    suggestions: [
      "How did the store revamp impact revenue?",
      "Walk me through the key UX decisions",
      "What metrics did you track for success?",
    ],
  },
  {
    keywords: ['bundle', 'offer', 'bonus cash', 'currency', 'margin'],
    suggestions: [
      "How did bundles reduce bonus cash dependence?",
      "What was the A/B testing strategy for bundles?",
      "How did you model the analytics for this?",
    ],
  },
  {
    keywords: ['verify', 'kick', 'sneaker', 'authentic', 'fake'],
    suggestions: [
      "How accurate is the AI authentication?",
      "What's the tech stack behind Verify Kicks?",
      "What was the hardest problem to solve?",
    ],
  },
  {
    keywords: ['pm', 'product manager', 'product management', 'career', 'journey', 'role'],
    suggestions: [
      "What's your product management philosophy?",
      "What project are you most proud of?",
      "How do you approach prioritization?",
    ],
  },
  {
    keywords: ['skillz', 'gaming', 'mobile', 'esports'],
    suggestions: [
      "Tell me about the Skillz Store project",
      "What did you build with Bundled Offers?",
      "What was your biggest win at Skillz?",
    ],
  },
  {
    keywords: ['education', 'school', 'degree', 'university', 'background'],
    suggestions: [
      "What work experience do you have?",
      "What projects have you shipped?",
      "What makes you stand out as a PM?",
    ],
  },
];

const DEFAULT_FOLLOW_UPS = [
  "Tell me about the Skillz Store",
  "What's Verify Kicks?",
  "What makes you stand out as a PM?",
  "How do you approach A/B testing?",
];


const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STATE_ABBR: Record<string, string> = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA',
  'Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS',
  'Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA',
  'Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT',
  'Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM',
  'New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK',
  'Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC',
  'South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT',
  'Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY',
};

function StateLabels({ geographies }: { geographies: any[] }) {
  const { path } = useMapContext();
  return (
    <g>
      {geographies.map(geo => {
        const centroid = path.centroid(geo);
        if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return null;
        const abbr = STATE_ABBR[geo.properties.name];
        if (!abbr) return null;
        const offsets: Record<string, [number, number]> = {
          FL: [14, 6], MI: [8, 10], LA: [5, 0], MD: [8, -4], DE: [6, 0], NJ: [6, 0], RI: [4, 0], CT: [0, 4], MA: [2, -3],
        };
        const [ox, oy] = offsets[abbr] ?? [0, 0];
        return (
          <text
            key={`label-${geo.rsmKey}`}
            x={centroid[0] + ox}
            y={centroid[1] + oy}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 6, fill: 'rgba(255,255,255,0.3)', fontFamily: 'sans-serif', fontWeight: 700, pointerEvents: 'none', userSelect: 'none', letterSpacing: '0.5px' }}
          >
            {abbr}
          </text>
        );
      })}
    </g>
  );
}

const TRAVEL_CITIES: { name: string; coordinates: [number, number]; icon?: string }[] = [
  { name: "Seattle", coordinates: [-122.3321, 47.6062] },
  { name: "San Francisco", coordinates: [-122.4194, 37.7749] },
  { name: "San Jose", coordinates: [-121.8863, 37.3382] },
  { name: "Monterey", coordinates: [-121.8947, 36.6002] },
  { name: "Los Angeles", coordinates: [-118.2437, 34.0522] },
  { name: "San Diego", coordinates: [-117.1611, 32.7157] },
  { name: "Sedona", coordinates: [-111.7609, 34.8697] },
  { name: "Las Vegas", coordinates: [-115.1398, 36.1699], icon: 'home' },
  { name: "Denver", coordinates: [-104.9903, 39.7392] },
  { name: "Dallas", coordinates: [-96.797, 32.7767] },
  { name: "Houston", coordinates: [-95.3698, 29.7604] },
  { name: "Myrtle Beach", coordinates: [-78.8867, 33.6891] },
  { name: "Charleston", coordinates: [-79.9311, 32.7765] },
  { name: "Raleigh/Durham", coordinates: [-78.8986, 35.994] },
  { name: "Washington DC", coordinates: [-77.0369, 38.9072] },
  { name: "New York", coordinates: [-74.006, 40.7128] },
  { name: "New Jersey", coordinates: [-74.1724, 40.7357] },
  { name: "Ithaca", coordinates: [-76.4966, 42.4931] },
  { name: "Providence", coordinates: [-71.4128, 41.824] },
  { name: "Boston", coordinates: [-71.0589, 42.3601] },
  { name: "New Hampshire", coordinates: [-71.537, 42.9956] },
  { name: "Vermont", coordinates: [-73.2121, 44.4759] },
  { name: "Atlanta", coordinates: [-84.388, 33.749] },
];

const NATIONAL_PARKS: { name: string; coordinates: [number, number] }[] = [
  { name: "Rocky Mountain", coordinates: [-105.6836, 40.3428] },
  { name: "Death Valley", coordinates: [-116.9325, 36.5054] },
  { name: "Bryce Canyon", coordinates: [-112.1871, 37.593] },
  { name: "Zion", coordinates: [-113.0263, 37.2982] },
  { name: "Lower Antelope Canyon", coordinates: [-111.3748, 36.8619] },
  { name: "Grand Canyon", coordinates: [-112.1401, 36.0544] },
  { name: "Congaree", coordinates: [-80.7837, 33.7948] },
];

const FLIGHT_LINES: { from: [number, number]; to: [number, number] }[] = [
  { from: [-96.797, 32.7767],   to: [-78.8986, 35.994] },  // Dallas ↔ Raleigh
  { from: [-74.006, 40.7128],   to: [-78.8986, 35.994] },  // NY ↔ Raleigh
  { from: [-74.006, 40.7128],   to: [-76.4966, 42.4931] },  // NY ↔ Ithaca
  { from: [-104.9903, 39.7392], to: [-118.2437, 34.0522] }, // Denver ↔ LA
  { from: [-117.1611, 32.7157], to: [-118.2437, 34.0522] }, // SD ↔ LA
  { from: [-118.2437, 34.0522], to: [-121.8947, 36.6002] }, // LA ↔ Monterey
  { from: [-121.8947, 36.6002], to: [-121.8863, 37.3382] }, // Monterey ↔ San Jose
  { from: [-121.8863, 37.3382], to: [-122.4194, 37.7749] }, // San Jose ↔ SF
  { from: [-78.8986, 35.994],  to: [-115.1398, 36.1699] }, // Raleigh ↔ Las Vegas
  { from: [-77.0369, 38.9072],  to: [-78.8986, 35.994] },  // DC ↔ Raleigh
  { from: [-96.797, 32.7767],   to: [-115.1398, 36.1699] }, // Dallas ↔ Las Vegas
  { from: [-71.0589, 42.3601],  to: [-115.1398, 36.1699] }, // Boston ↔ Las Vegas
  { from: [-104.9903, 39.7392], to: [-115.1398, 36.1699] }, // Denver ↔ Las Vegas
  { from: [-122.4194, 37.7749], to: [-115.1398, 36.1699] }, // SF ↔ Las Vegas
  { from: [-115.1398, 36.1699], to: [-117.1611, 32.7157] }, // Las Vegas ↔ San Diego
  { from: [-115.1398, 36.1699], to: [-122.3321, 47.6062] }, // Las Vegas ↔ Seattle
  { from: [-115.1398, 36.1699], to: [-84.388, 33.749] },    // Las Vegas ↔ Atlanta
  { from: [-115.1398, 36.1699], to: [-111.7609, 34.8697] }, // Las Vegas ↔ Sedona
  { from: [-96.797, 32.7767],   to: [-95.3698, 29.7604] },  // Dallas ↔ Houston
  { from: [-78.8986, 35.994],   to: [-79.9311, 32.7765] },  // Raleigh ↔ Charleston
  { from: [-79.9311, 32.7765],  to: [-78.8867, 33.6891] },  // Charleston ↔ Myrtle Beach
  { from: [-78.8867, 33.6891],  to: [-115.1398, 36.1699] }, // Myrtle Beach ↔ Las Vegas
  { from: [-115.1398, 36.1699], to: [-71.4128, 41.824] },   // Las Vegas ↔ Providence
  { from: [-71.4128, 41.824],   to: [-71.537, 42.9956] },   // Providence ↔ New Hampshire
  { from: [-71.537, 42.9956],   to: [-73.2121, 44.4759] },  // New Hampshire ↔ Vermont
];

const SIDE_PROJECTS = [
  { title: 'Ledger', description: 'Ledger takes your bank CSV, auto-categorizes every transaction, and surfaces insights on your spending patterns - so you can make smarter money decisions.', tags: ['AI', 'Finance', 'Productivity'], bg: 'bg-gradient-to-br from-emerald-700 to-slate-900', image: '/ledger.png', icon: null },
  { title: 'Snip', description: 'Press ⌘⇧2 and your capture stays on screen. Stack multiple, pin the most important one, and pull up your capture history anytime.', tags: ['Swift', 'macOS', 'Utility'], bg: 'bg-gradient-to-br from-indigo-700 to-slate-900', image: '/snip.png', icon: null },
  { title: 'Plateful', description: 'No more wondering what to cook. Every Sunday at 9PM you get a full week of meals, picked from your own dish library.', tags: ['AI', 'Automation', 'Productivity'], bg: 'bg-gradient-to-br from-amber-700 to-stone-900', image: '/planner.png', icon: null },
];

const usedSuggestions = new Set<string>();

function getFollowUpSuggestions(response: string): string[] {
  const lower = response.toLowerCase();
  const allContextual = CONTEXTUAL_FOLLOW_UPS.flatMap(c =>
    c.keywords.some(kw => lower.includes(kw)) ? c.suggestions : []
  );
  const pool = allContextual.length > 0 ? allContextual : DEFAULT_FOLLOW_UPS;
  const fresh = pool.filter(s => !usedSuggestions.has(s));
  const source = fresh.length >= 3 ? fresh : pool;
  const picked = [...source].sort(() => Math.random() - 0.5).slice(0, 3);
  picked.forEach(s => usedSuggestions.add(s));
  return picked;
}

function CustomCursor() {
  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: '22vw',
        top: '42vh',
        pointerEvents: 'none',
        zIndex: 15,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '6px',
        opacity: 0.75,
        animation: 'saum-drift 20s ease-in-out infinite',
      }}
    >
      <svg width="16" height="20" viewBox="0 0 16 20" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1 1L14 8.5L8.5 10.5L6.5 18L1 1Z" fill="white" stroke="rgba(0,0,0,0.25)" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
      <div
        style={{
          background: '#4B7BF5',
          color: 'white',
          padding: '4px 10px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 12px rgba(75,123,245,0.35)',
          animation: 'saum-label-pulse 3.5s ease-in-out infinite',
          marginTop: '1px',
        }}
      >
        Saum
      </div>
    </div>,
    document.body
  );
}

function App() {
  const [showChat, setShowChat] = useState(false);
  const [machineMode, setMachineMode] = useState(false);
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [showGame, setShowGame] = useState(false);
  const [gameCards, setGameCards] = useState<{id: number; emoji: string; isMatched: boolean}[]>([]);
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matchedCount, setMatchedCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [mismatch, setMismatch] = useState<number[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [pillInput, setPillInput] = useState('');
  const [pillFocused, setPillFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [responseCount, setResponseCount] = useState(0);
  const [sideProjectIndex, setSideProjectIndex] = useState(1);
  const carouselPaused = useRef(false);
  const [hoveredGeo, setHoveredGeo] = useState<string | null>(null);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [mapView, setMapView] = useState<'cities' | 'parks'>('cities');

  useEffect(() => {
    const timer = setInterval(() => {
      if (!carouselPaused.current) {
        setSideProjectIndex(i => (i + 1) % SIDE_PROJECTS.length);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, []);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typedRef = useRef<HTMLSpanElement>(null);
  const typed = useRef<any>(null);
  const [playMessageSound] = useSound('/message.mp3');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (machineMode) return;
    if (typedRef.current) {
      typed.current = new Typed(typedRef.current, {
        strings: ['Product Manager', 'AI Enthusiast', 'Design Thinker'],
        typeSpeed: 50,
        backSpeed: 30,
        backDelay: 3000,
        loop: true,
        showCursor: false,
      });
    }
    return () => { typed.current?.destroy(); };
  }, [machineMode]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi there! I'm SaumAI, Saumya's personal AI assistant.\n\nI'd be happy to tell you about his education, work experience, or any other aspect of his professional journey. What would you like to know?",
      }]);
    }
  }, []);


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const hasUserMessages = messages.some(m => m.role === 'user');

  const sendMessage = async (text: string) => {
    if (responseCount >= MAX_RESPONSES) {
      setMessages(prev => [...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: "I've reached my response limit for this session. Please refresh the page to start a new conversation." },
      ]);
      return;
    }

    const userMessage = { role: 'user' as const, content: text };
    setMessages(prev => [...prev, userMessage]);
    setFollowUpSuggestions([]);
    setIsTyping(true);

    try {
      const response = await fetch(`https://kowvjmkfoxeeidwiphvy.supabase.co/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtvd3ZqbWtmb3hlZWlkd2lwaHZ5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEyMDQ0ODYsImV4cCI6MjA4Njc4MDQ4Nn0.JODio5rBinjzOIbK5wXYcfT0en8gPZOojrqKjbv2ANY`,
          'Apikey': `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtvd3ZqbWtmb3hlZWlkd2lwaHZ5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzEyMDQ0ODYsImV4cCI6MjA4Njc4MDQ4Nn0.JODio5rBinjzOIbK5wXYcfT0en8gPZOojrqKjbv2ANY`,
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(msg => ({ role: msg.role, content: msg.content })),
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');
      const data = await response.json();
      const assistantResponse = data.response || "I apologize, but I couldn't generate a response. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
      setFollowUpSuggestions(getFollowUpSuggestions(assistantResponse));
      setResponseCount(prev => prev + 1);
      playMessageSound();
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const initGame = () => {
    const emojis = ['🤖', '🔋', '⚙️', '💻'];
    const cards = [...emojis, ...emojis]
      .map((emoji, i) => ({ id: i, emoji, isMatched: false }))
      .sort(() => Math.random() - 0.5);
    setGameCards(cards);
    setFlipped([]);
    setMatchedCount(0);
    setIsChecking(false);
    setMismatch([]);
  };

  const handleCardClick = (index: number) => {
    if (isChecking || gameCards[index]?.isMatched || flipped.includes(index) || flipped.length === 2) return;
    const newFlipped = [...flipped, index];
    setFlipped(newFlipped);
    if (newFlipped.length === 2) {
      const [a, b] = newFlipped;
      if (gameCards[a].emoji === gameCards[b].emoji) {
        const updated = gameCards.map((c, i) => i === a || i === b ? { ...c, isMatched: true } : c);
        setGameCards(updated);
        setFlipped([]);
        const next = matchedCount + 1;
        setMatchedCount(next);
        if (next === 4) {
          setTimeout(() => {
            setShowGame(false);
            setMachineMode(true);
            setMatchedCount(0);
            setMismatch([]);
          }, 900);
        }
      } else {
        setIsChecking(true);
        setMismatch([a, b]);
        setTimeout(() => { setFlipped([]); setIsChecking(false); setMismatch([]); }, 900);
      }
    }
  };

  const handlePillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pillInput.trim()) return;
    const text = pillInput.trim();
    setPillInput('');
    setShowChat(true);
    await sendMessage(text);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input.trim();
    setInput('');
    await sendMessage(text);
  };

  const handleSuggestion = async (text: string) => {
    await sendMessage(text);
  };

  return (
    <div className={`min-h-screen flex flex-col transition-all duration-300 ${machineMode ? 'bg-black' : theme === 'dark' ? 'bg-black' : 'bg-white'} ${showChat && !machineMode ? 'md:mr-[504px]' : ''}`}>
      {!machineMode && <CustomCursor />}

      {/* HUMAN / MACHINE toggle — top right, hidden when sidebar is open */}
      <div className={`fixed top-6 right-6 z-[70] transition-all duration-200 ${showChat ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="flex items-center gap-3 bg-zinc-900 rounded-md px-3.5 py-2">
          <button onClick={() => setMachineMode(false)} className="flex items-center gap-1.5 transition-all">
            <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${!machineMode ? 'border-white' : 'border-zinc-400'}`}>
              {!machineMode && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </span>
            <span className={`text-[11px] font-medium tracking-widest transition-all ${!machineMode ? 'text-white' : 'text-zinc-400'}`}>HUMAN</span>
          </button>
          <button onClick={() => { if (!machineMode) { initGame(); setShowGame(true); } }} className="flex items-center gap-1.5 transition-all">
            <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${machineMode ? 'border-white' : 'border-zinc-400'}`}>
              {machineMode && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </span>
            <span className={`text-[11px] font-medium tracking-widest transition-all ${machineMode ? 'text-white' : 'text-zinc-400'}`}>MACHINE</span>
          </button>
        </div>
      </div>

      {/* Machine mode — terminal view */}
      {machineMode && (
        <div className="flex justify-center px-8 pt-20 pb-16">
          <div className="w-full max-w-[780px]">
            <div className="rounded-xl border border-zinc-800 overflow-hidden bg-[#0d0d0d]">
              <div className="flex items-center gap-1.5 px-4 py-3 bg-zinc-900 border-b border-zinc-800 sticky top-0 z-10">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
                <span className="ml-3 text-zinc-500 text-xs font-mono">saumya@portfolio ~ </span>
              </div>
              <div className="p-8 font-mono text-sm leading-relaxed space-y-5 text-zinc-300">

                {/* ── HERO ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat hero.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1">
                  <div className="text-white text-lg font-bold">Saumya Patni</div>
                  <div className="text-zinc-400">Product Manager · AI Enthusiast · Design Thinker</div>
                  <div className="text-zinc-500">Las Vegas, Nevada · saum.patni@gmail.com · saumyapatni.xyz</div>
                </div>

                {/* ── ABOUT ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat about.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1 text-zinc-400">
                  <div>Hey, I'm Saumya (Saum) — a Product Manager at Skillz (NYSE: SKLZ) focused on retention and monetization strategies.</div>
                  <div className="pt-1">I recently completed my Master's of Engineering Management (Product Management) at Duke University with a perfect 4.0 GPA, and hold a Bachelor's in Computer Science from NMIMS, India (3.83 GPA, Dean's Merit List — Gold Medal).</div>
                  <div className="pt-1">I'm passionate about building consumer products, innovation, and researching ideas. In the future, I plan on doing angel investing in startups as a hobby — or perhaps even build my own venture.</div>
                  <div className="pt-1"><span className="text-zinc-500">hobbies:  </span>Cricket · Formula 1 · Live music · Stock market</div>
                  <div><span className="text-zinc-500">travel:   </span>7/195 countries — UAE, Singapore, Thailand, Malaysia, China, Indonesia, USA</div>
                  <div><span className="text-zinc-500">bucket:   </span>#32 — fly a plane ✈</div>
                </div>

                {/* ── IMPACT & WORK ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">ls impact-and-work/</span></div>
                <div className="pl-3 text-blue-400">skillz-store/&nbsp;&nbsp; bundles/&nbsp;&nbsp; verify-kicks/</div>

                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat impact-and-work/skillz-store.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-2">
                  <div className="text-white font-bold">Skillz Store</div>
                  <div className="text-zinc-400">Revamped Store UI to boost user deposits through modern design.</div>
                  <div className="text-zinc-500">Redesigned Skillz's deposit store to fix a critical problem — our store looked outdated and lacked excitement factor. Leading a complete UX overhaul with new engagement features helped drive $$ in incremental annual revenue.</div>
                  <div className="text-zinc-600">tags: Product Management · User Research · A/B Testing · Competitive Analysis · Product Documentation · UX Design</div>
                  <button onClick={() => setExpandedProject(expandedProject === 'skillz-store' ? null : 'skillz-store')} className="text-blue-400 hover:text-blue-300 text-xs font-mono transition-colors">
                    {expandedProject === 'skillz-store' ? '▲ collapse' : '▼ read more'}
                  </button>
                  {expandedProject === 'skillz-store' && (
                    <div className="space-y-3 pt-1 border-t border-zinc-800 mt-2">
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat skillz-store/overview.txt</span></div>
                      <div className="text-zinc-400 pl-3">We redesigned Skillz's deposit store experience across multiple features to solve a fundamental problem: our outdated interface wasn't converting users or showcasing our offers effectively. Leading the execution across 4 major features, our team drove incremental annual revenue.</div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat skillz-store/problem.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>The existing deposit store had a basic, outdated design that failed to engage users. LTOs had poor visibility, and the overall experience lacked the modern feel needed to drive conversions.</div>
                        <div className="text-zinc-500">  └─ Users weren't depositing as much as they could</div>
                        <div className="text-zinc-500">  └─ Offers were buried and hard to discover</div>
                        <div className="text-zinc-500">  └─ The store felt dated compared to 15+ competitors analyzed</div>
                        <div className="pt-1">Through competitive analysis of 15+ platforms, I identified 3 key issues: poor offer visibility, lack of engagement mechanics, and missed monetization opportunities.</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat skillz-store/solution.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>Led end-to-end redesign across 4 core features with engineering, design, analytics, and marketing teams:</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">LTO Carousel Redesign</span> — auto-rotating carousel showcasing full offer value upfront, swipeable by users</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Smart Offer Nudges</span> — contextual nudges after 5 losses, when balance hits $0, strategic re-pops</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Deposit Success Screen</span> — celebratory animations + upsell on discounted game templates</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Payment Flow Improvements</span> — streamlined flow reducing friction at checkout</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat skillz-store/impact.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div className="text-zinc-500">  └─ Total deposits improved across all offer types</div>
                        <div className="text-zinc-500">  └─ Install-to-deposit rate significantly higher than control group</div>
                        <div className="text-zinc-500">  └─ Deposits through LTOs and deposit slates both increased</div>
                        <div className="text-zinc-500">  └─ Generated incremental annual revenue</div>
                      </div>
                    </div>
                  )}
                </div>

                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat impact-and-work/bundles.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-2">
                  <div className="text-white font-bold">Skillz Bundled Offers</div>
                  <div className="text-zinc-400">Defined offer strategies to reduce incentives dependence &amp; improve margins.</div>
                  <div className="text-zinc-500">Led the strategy and configuration of Bundled Offers — combining real cash, bonus cash, Z's, and Tickets into multi-currency bundles. Reduced over-reliance on bonus cash and increased offer engagement through analytics modeling and A/B testing.</div>
                  <div className="text-zinc-600">tags: Product Management · Analytics Modeling · A/B Testing · Monetization Strategy · Competitive Analysis · Product Documentation</div>
                  <button onClick={() => setExpandedProject(expandedProject === 'bundles' ? null : 'bundles')} className="text-blue-400 hover:text-blue-300 text-xs font-mono transition-colors">
                    {expandedProject === 'bundles' ? '▲ collapse' : '▼ read more'}
                  </button>
                  {expandedProject === 'bundles' && (
                    <div className="space-y-3 pt-1 border-t border-zinc-800 mt-2">
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat bundles/overview.txt</span></div>
                      <div className="text-zinc-400 pl-3">As PM, I led the strategy and configuration of Bundled Offers — a net-new monetization framework built on top of Skillz's existing deposit offers. The goal: move beyond flat bonus cash offers and create multi-currency bundles that drove higher deposit conversion while reducing over-reliance on bonus cash.</div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat bundles/problem.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>Skillz's deposit offers were structured as a straightforward bonus cash calculation — deposit X, get Y bonus cash. Two compounding problems:</div>
                        <div className="text-zinc-500">  └─ Over-reliance on bonus cash was eroding margins</div>
                        <div className="text-zinc-500">  └─ Offers lacked perceived variety or value, limiting engagement</div>
                        <div className="text-zinc-500">  └─ Z's and Ticketz were underutilized as monetization levers</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat bundles/solution.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>Built a new bundled offers framework from scratch, introducing two bundle types:</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Cash + Bonus Cash + Z's</span> — Z's added perceived value at near-zero margin cost</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Cash + Bonus Cash + Ticketz</span> — tied deposits to loyalty progression, increasing retention</div>
                        <div className="pt-1">Validated through analytics modeling and A/B testing across multiple user segments.</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat bundles/impact.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div className="text-zinc-500">  └─ Measurably reduced bonus cash reliance, improving deposit margins</div>
                        <div className="text-zinc-500">  └─ Drove higher deposit conversion rates vs. control group</div>
                        <div className="text-zinc-500">  └─ Improved offer engagement across bundled SKUs</div>
                        <div className="text-zinc-500">  └─ Successfully increased perceived value of Z's and Ticketz</div>
                        <div className="text-zinc-500">  └─ Generated meaningful incremental annual revenue</div>
                      </div>
                    </div>
                  )}
                </div>

                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat impact-and-work/verify-kicks.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-2">
                  <div className="text-white font-bold">Verify Kicks</div>
                  <div className="text-zinc-400">AI-powered sneaker authentication for real buyers.</div>
                  <div className="text-zinc-500">I built Verify Kicks to solve a simple problem — people keep getting scammed by fake sneakers online. Using AI, our app authenticates shoes in seconds with just a few photos. It's fast, accurate, and designed for trust.</div>
                  <div className="text-zinc-600">tags: Product Management · User Research · Product Strategy · UX Design</div>
                  <button onClick={() => setExpandedProject(expandedProject === 'verify-kicks' ? null : 'verify-kicks')} className="text-blue-400 hover:text-blue-300 text-xs font-mono transition-colors">
                    {expandedProject === 'verify-kicks' ? '▲ collapse' : '▼ read more'}
                  </button>
                  {expandedProject === 'verify-kicks' && (
                    <div className="space-y-3 pt-1 border-t border-zinc-800 mt-2">
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat verify-kicks/overview.txt</span></div>
                      <div className="text-zinc-400 pl-3">Verify Kicks is an AI-powered sneaker authentication app that helps buyers instantly confirm whether their sneakers are real or fake. I led the product vision, strategy, and user validation — working with AI engineers who built and trained the computer-vision model.</div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat verify-kicks/problem.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>Counterfeit sneakers are everywhere. Manual authentication services exist, but they're slow, expensive, and not scalable. 15 user interviews revealed 3 consistent pain points:</div>
                        <div className="text-zinc-500">  └─ Trust issues — buyers couldn't be sure their sneakers were real</div>
                        <div className="text-zinc-500">  └─ Manual verification — experts took days to respond</div>
                        <div className="text-zinc-500">  └─ Financial risk — people lost hundreds on fakes</div>
                        <div className="pt-1">80% of respondents said they'd use an app for quick authentication.</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat verify-kicks/solution.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div>Designed a simple three-step experience: Scan → Verify → Trust.</div>
                        <div className="text-zinc-500">  └─ Upload multi-angle photos → receive authentic/fake result with confidence score</div>
                        <div className="text-zinc-500">  └─ AI stack: YOLOv12 for image detection, FastAPI backend, AWS S3 + MongoDB</div>
                        <div className="text-zinc-500">  └─ <span className="text-zinc-300">Tiered pricing</span> — Saver (24hrs) · Flex (12hrs) · Anytime (instant)</div>
                        <div className="text-zinc-500">  └─ $9B TAM · $360M SAM · $15M SOM</div>
                      </div>
                      <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat verify-kicks/impact.txt</span></div>
                      <div className="pl-3 space-y-1 text-zinc-400">
                        <div className="text-zinc-500">  └─ 80% willingness to use the app among surveyed users</div>
                        <div className="text-zinc-500">  └─ NPS score of 70 — high satisfaction in validation</div>
                        <div className="text-zinc-500">  └─ 2 sneaker influencers ready to promote at launch</div>
                        <div className="text-zinc-500">  └─ Pricing $2.99–$19.99/check — affordable and scalable</div>
                        <div className="text-zinc-500">  └─ B2B expansion path: API integrations for resale platforms</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── EXPERIENCE ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/experience.log</span></div>
                <div className="pl-3 space-y-4">
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Skillz (NYSE: SKLZ)</span><span className="text-zinc-500"> · Las Vegas, NV · Jun 2025 – Present</span></div>
                    <div className="text-zinc-400">Product Manager, Co-op</div>
                    <div className="text-zinc-500">  └─ Redesigned in-app payment experience via competitive analysis of 10+ platforms &amp; user research — significantly increased annual revenue</div>
                    <div className="text-zinc-500">  └─ Owned product roadmap &amp; PRD for new pricing and platform features, defining MVP scope and success metrics — drove meaningful lift in first-purchase conversion</div>
                    <div className="text-zinc-500">  └─ Identified payment fraud pattern, led cross-functional response implementing prevention controls that substantially reduced chargebacks</div>
                    <div className="text-zinc-500">  └─ Drove cost optimization replacing cash incentives with virtual rewards while maintaining average deposit amounts</div>
                    <div className="text-zinc-500">  └─ Led monetization strategy for post-deposit game recommendations — strong engagement and incremental transaction volume</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Quantiphi Inc.</span><span className="text-zinc-500"> · Mumbai, India · Aug 2022 – Jun 2024</span></div>
                    <div className="text-zinc-400">Associate Product Manager</div>
                    <div className="text-zinc-500">  └─ Launched AI chatbot for Missouri Dept. of Social Services — reduced call wait times by 70%, resolved 50% of 32K monthly inquiries without human agents</div>
                    <div className="text-zinc-500">  └─ Defined product strategy for AI chatbot platform serving 7 U.S. public sector clients — automated 32% of support queries, saved $2.4M annually</div>
                    <div className="text-zinc-500">  └─ Created PRDs, product roadmaps, prioritized backlog in JIRA — reduced TTM by 15%, aligned 3 teams on delivery goals</div>
                    <div className="text-zinc-500">  └─ Built 3 Looker Studio dashboards tracking resolution rate, user satisfaction, intent patterns — 18% reduction in churn</div>
                    <div className="text-zinc-500">  └─ Led Google CCAI implementation for Sullivan County — reduced routine call volume by 62%, drove adoption across 40+ county offices</div>
                    <div className="text-zinc-500">  └─ Showcased Conversational AI at Google Next 2024 — 200% surge in pipeline, secured new business opportunities</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Sosha Design Studio</span><span className="text-zinc-500"> · India · Aug 2020 – Jul 2021</span></div>
                    <div className="text-zinc-400">Product Manager Intern</div>
                    <div className="text-zinc-500">  └─ Launched 5 e-commerce platforms on WordPress &amp; Shopify — A/B testing led to 25% increase in conversion rates</div>
                    <div className="text-zinc-500">  └─ Boosted user engagement by 30% through SEO optimization and refined acquisition funnels via Google Analytics</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Blitz Jobs / Nyra Naturals</span><span className="text-zinc-500"> · India · May – Jun 2021</span></div>
                    <div className="text-zinc-400">Web Development</div>
                    <div className="text-zinc-500">  └─ End-to-end development of e-commerce website using HTML, CSS, PHP, WordPress — 40% sales increase in first 3 months</div>
                  </div>
                </div>

                {/* ── PROJECTS ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/projects.txt</span></div>
                <div className="pl-3 space-y-4">
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Texture Tech</span><span className="text-zinc-500"> · Durham, NC · Aug – Dec 2024</span></div>
                    <div className="text-zinc-400">Student Consultant — B2C startup, tech-driven cosmetic/hair care</div>
                    <div className="text-zinc-500">  └─ Uncovered $2.5B SOM opportunity via VRIO &amp; strategic group analysis</div>
                    <div className="text-zinc-500">  └─ Led 40+ user research interviews — 3 physical prototypes, NPS 23 → 60</div>
                    <div className="text-zinc-500">  └─ Mapped JTBD to user journeys — boosted awareness by 70%</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Athletic Recovery Center (ARC)</span><span className="text-zinc-500"> · Cary, NC · Aug – Dec 2024</span></div>
                    <div className="text-zinc-400">Student Consultant — B2C wellness startup</div>
                    <div className="text-zinc-500">  └─ GTM strategy differentiating from 6 competitors through service packages</div>
                    <div className="text-zinc-500">  └─ Tiered pricing strategy — recouped $140K upfront investment within year 1</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Verify Kicks</span><span className="text-zinc-500"> · Durham, NC · Sep 2024 – Jan 2025</span></div>
                    <div className="text-zinc-400">Founder — AI-powered sneaker authentication web app</div>
                    <div className="text-zinc-500">  └─ TAM $9B · SAM $360M · SOM $15M via quantitative research &amp; VRIO analysis</div>
                    <div className="text-zinc-500">  └─ Built &amp; validated AI-powered MVP with v0 + Bolt.new, tested with 25+ users</div>
                    <div className="text-zinc-500">  └─ Presented at Duke Fuqua Startup Meet</div>
                  </div>
                </div>

                {/* ── EDUCATION ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/education.txt</span></div>
                <div className="pl-3 space-y-3">
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-yellow-400 font-bold">Duke University</span><span className="text-zinc-500"> · Durham, NC · Aug 2024 – Dec 2025</span></div>
                    <div className="text-zinc-400">Master of Engineering Management, Product Management <span className="text-green-400">GPA: 4.0/4.0</span></div>
                    <div className="text-zinc-500">  └─ MEM Program Ambassador · President, Duke MEM Investment Club</div>
                    <div className="text-zinc-500">  └─ Coursework: Design Thinking · AI Entrepreneurship · Product Management · Product Marketing · Digital Transformation</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-yellow-400 font-bold">NMIMS University</span><span className="text-zinc-500"> · Mumbai, India · Jul 2018 – Aug 2022</span></div>
                    <div className="text-zinc-400">B.Tech, Computer Science Engineering <span className="text-green-400">GPA: 3.83/4.0</span></div>
                    <div className="text-zinc-500">  └─ Dean's Merit List (Gold Medal)</div>
                    <div className="text-zinc-500">  └─ Coursework: Machine Learning · AI · Data Visualization · Database Systems · Web Development</div>
                  </div>
                </div>

                {/* ── SKILLS ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/skills.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1">
                  <div><span className="text-zinc-500">languages:   </span>SQL · Python (Intermediate)</div>
                  <div><span className="text-zinc-500">tools:       </span>JIRA · Asana · Confluence · Figma · Miro · Notion · Cursor · Claude Code · Bolt · Power BI · Tableau · Looker Studio · Google Analytics · PostHog · Canva</div>
                  <div><span className="text-zinc-500">platforms:   </span>WordPress · Shopify · Bolt.new · v0 (No-Code)</div>
                  <div><span className="text-zinc-500">data:        </span>SQL · Snowflake · Power BI · Tableau · Looker Studio · Google Analytics · PostHog</div>
                  <div><span className="text-zinc-500">methods:     </span>Agile · Scrum · Design Thinking · A/B Testing · OKRs · PRDs · Roadmapping</div>
                  <div><span className="text-zinc-500">soft skills: </span>Communication · EQ · Adaptability · Problem-Solving · Cross-functional Leadership · Creativity · Negotiation</div>
                </div>

                {/* ── CERTIFICATIONS ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/certifications.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1 text-zinc-400">
                  {['Product Management: Building a Product Roadmap','Product Management: Building a Product Strategy','Associate Cloud Engineer (Google)','Generative AI Learning Path','Enterprise Design Thinking Practitioner','Professional Scrum Master 1 (PSM I)'].map(c => (
                    <div key={c}><span className="text-green-400">✓ </span>{c}</div>
                  ))}
                </div>

                {/* ── LEADERSHIP ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/leadership.txt</span></div>
                <div className="pl-3 space-y-3">
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Duke University — MEM Program Ambassador</span><span className="text-zinc-500"> · Dec 2024 – Jun 2025</span></div>
                    <div className="text-zinc-500">  └─ New initiatives driving program enhancements · connected with 30+ incoming students</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">SK Student Mentorship — Co-Founder</span><span className="text-zinc-500"> · Jun – Aug 2024</span></div>
                    <div className="text-zinc-500">  └─ Built nationwide Topmate community · mentored 35+ individuals · supported 200+ students</div>
                    <div className="text-zinc-500">  └─ Hosted 3 webinars on application building &amp; interview prep</div>
                  </div>
                  <div className="border-l-2 border-zinc-700 pl-3 space-y-1">
                    <div><span className="text-blue-400 font-bold">Jio Studios — Head, Artist &amp; Guest Management</span><span className="text-zinc-500"> · Dec 2019</span></div>
                    <div className="text-zinc-500">  └─ Led artist coordination for Comics for Change (supported by UN Women &amp; Kailash Satyarthi Foundation)</div>
                  </div>
                </div>

                {/* ── HONORS ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat resume/honors.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1 text-zinc-400">
                  <div><span className="text-yellow-400">★ </span>Runner Up — Social Conclave 2019</div>
                  <div><span className="text-yellow-400">★ </span>International Rank — 11th National Science Olympiad (NSO)</div>
                  <div><span className="text-yellow-400">★ </span>Dean's Merit List (BTech 2018–2022)</div>
                  <div><span className="text-yellow-400">★ </span>MEM Program Ambassador — Duke University</div>
                </div>

                {/* ── CONTACT ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">cat contact.txt</span></div>
                <div className="pl-3 border-l-2 border-zinc-700 space-y-1">
                  <div><span className="text-zinc-500">email     </span>saum.patni@gmail.com</div>
                  <div><span className="text-zinc-500">linkedin  </span>linkedin.com/in/saumya-patni</div>
                  <div><span className="text-zinc-500">instagram </span>instagram.com/saumyapatni</div>
                  <div><span className="text-zinc-500">website   </span>saumyapatni.xyz</div>
                  <div><span className="text-zinc-500">location  </span>Las Vegas, NV — open to relocation &amp; travel</div>
                  <div><span className="text-zinc-500">visa      </span>F1 — will require H1B sponsorship for full-time roles</div>
                </div>

                {/* ── FOOTER ── */}
                <div><span className="text-green-400">$ </span><span className="text-white font-bold">echo "Nice to meet you!"</span></div>
                <div className="pl-3 text-zinc-400">Nice to meet you!</div>
                <div className="pl-3 text-zinc-600">© {new Date().getFullYear()} Saumya Patni. All rights reserved.</div>


                <div className="flex items-center gap-2 pt-2">
                  <span className="text-green-400">$ </span>
                  <span className="inline-block w-[9px] h-[15px] bg-green-400" style={{ animation: 'blink 1s infinite' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {!machineMode && <main className="flex-1 flex items-start justify-center px-6 sm:px-10 md:px-16 pt-4 pb-4">
        {/* Side nav */}
        <nav className={`fixed left-[calc((100vw-1200px)/8)] top-1/2 -translate-y-1/2 rounded-full py-3 px-2 flex flex-col items-center gap-2 z-50 transition-all duration-300 ${
          theme === 'dark' ? 'bg-zinc-900/90 border border-white/10' : 'bg-gray-100/90 border border-gray-300'
        } backdrop-blur-xl`}>
          <Link to="/" className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
            theme === 'dark' ? 'bg-zinc-800/50 text-white/80 hover:bg-zinc-800 hover:text-white' : 'bg-gray-200/50 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
          }`}>
            <Home size={18} />
          </Link>
          <button
            onClick={() => document.getElementById('work-section')?.scrollIntoView({ behavior: 'smooth' })}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
              theme === 'dark' ? 'bg-zinc-800/50 text-white/80 hover:bg-zinc-800 hover:text-white' : 'bg-gray-200/50 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Briefcase size={18} />
          </button>
          <button
            onClick={() => document.getElementById('side-projects-section')?.scrollIntoView({ behavior: 'smooth' })}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
              theme === 'dark' ? 'bg-zinc-800/50 text-white/80 hover:bg-zinc-800 hover:text-white' : 'bg-gray-200/50 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <FlaskConical size={18} />
          </button>
        </nav>

        {/* Hero card */}
        <div className={`w-full max-w-[1200px] h-[calc(100vh-2rem)] rounded-2xl sm:rounded-3xl relative overflow-hidden transition-colors duration-300 ${
          theme === 'dark' ? 'bg-black border border-white/5' : 'bg-gray-50 border border-gray-200'
        }`}>
          {/* Scatter images */}
          <div className="scatter-img scatter-img-1" onClick={() => document.getElementById('bundles-section')?.scrollIntoView({ behavior: 'smooth' })}>
            <img src="/bundles.png" alt="Bundles" className="w-full h-full object-contain" />
          </div>
          <div className="scatter-img scatter-img-2 cursor-default" style={{ pointerEvents: 'none' }}>
            <img src="/custom_deposits.png" alt="Custom Deposits" className="w-full h-full object-contain" />
          </div>
          <div
            className="scatter-img scatter-img-3"
            onClick={() => {
              const el = document.getElementById('skillz-store-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
              else { window.location.hash = ''; setTimeout(() => document.getElementById('skillz-store-section')?.scrollIntoView({ behavior: 'smooth' }), 100); }
            }}
          >
            <img src="/bothimages.png" alt="Skillz Store" className="w-full h-full object-contain" />
          </div>
          <div className="scatter-img scatter-img-4" onClick={() => document.getElementById('verifykicks-section')?.scrollIntoView({ behavior: 'smooth' })}>
            <img src="/new.png" alt="Verify Kicks" className="w-full h-full object-contain" />
          </div>

          {/* Center content — name only */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
            <div className="text-center">
              <h1 className={`text-4xl sm:text-5xl md:text-7xl font-serif font-bold tracking-wide mb-3 transition-colors duration-300 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Saumya Patni
              </h1>
              <h2 className={`font-serif tracking-wide text-xl sm:text-2xl md:text-3xl transition-colors duration-300 ${
                theme === 'dark' ? 'text-white/80' : 'text-gray-600'
              }`} style={{ minHeight: '2.5rem' }}>
                <span ref={typedRef}></span>
              </h2>
            </div>
          </div>

          {/* Pill input — hidden when chat is open */}
          <div className={`fixed bottom-[8vh] left-1/2 -translate-x-1/2 z-20 transition-all duration-300 ${showChat ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <form onSubmit={handlePillSubmit}>
              <div
                className={`pill-border${pillFocused ? ' focused' : ''}`}
                style={{
                  width: pillFocused ? 'min(380px, calc(100vw - 3rem))' : '300px',
                  transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div className={`flex items-center rounded-full px-3 py-[11px] gap-3 transition-colors duration-300 ${
                  theme === 'dark' ? 'bg-black' : 'bg-white'
                }`}>
                  <img src="/saumai Background Removed.png" alt="Saum AI" className="w-7 h-7 object-cover flex-shrink-0" />
                  <input
                    type="text"
                    value={pillInput}
                    onChange={e => setPillInput(e.target.value)}
                    onFocus={() => setPillFocused(true)}
                    onBlur={() => setPillFocused(false)}
                    placeholder="Ask Saum AI"
                    className={`flex-1 min-w-0 bg-transparent outline-none text-sm transition-colors duration-300 ${
                      theme === 'dark' ? 'text-white placeholder-white/40' : 'text-gray-900 placeholder-gray-400'
                    }`}
                  />
                  {pillFocused && (
                    <button
                      type="submit"
                      onMouseDown={e => e.preventDefault()}
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                        theme === 'dark' ? 'bg-white text-black hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-700'
                      }`}
                    >
                      <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>}

      {/* Right-side chat panel */}
      {!machineMode && showChat && (
        <>
          {/* Dim overlay over left content */}
          <div className="fixed inset-0 md:right-[504px] bg-black/30 z-[54] pointer-events-none" />
          {/* Backdrop on mobile (also closes on tap) */}
          <div
            className="fixed inset-0 z-[55] bg-transparent md:hidden"
            onClick={() => setShowChat(false)}
          />
          <div className="fixed right-0 top-0 h-full w-full md:w-[504px] z-[60] flex flex-col animate-slide-from-right bg-white border-l border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-xl text-gray-900">Chat with Saum AI</h3>
                <p className="text-xs mt-0.5 text-gray-400">Powered by Claude</p>
              </div>
              <div className="flex items-center gap-5">
                {/* Text / Voice toggle */}
                <div className="flex items-center bg-gray-100 rounded-full p-1 gap-0.5">
                  <button className="px-4 py-1.5 rounded-full bg-white text-gray-900 text-sm font-medium shadow-sm">
                    Text
                  </button>
                  <div className="relative">
                    <button disabled className="px-4 py-1.5 rounded-full text-gray-400 text-sm font-medium cursor-not-allowed">
                      Voice
                    </button>
                    <span className="absolute -top-2 -right-1 bg-gray-300 text-gray-500 text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                      SOON
                    </span>
                  </div>
                </div>
                <button onClick={() => setShowChat(false)} className="text-gray-400 hover:text-gray-700 transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {/* Suggestions */}
              {!hasUserMessages && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs text-center mb-4 text-gray-400">Try asking:</p>
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all text-sm"
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Chat messages */}
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <img src="/saumai Background Removed.png" alt="Saum AI" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
                  )}
                  <div className="flex flex-col gap-1 max-w-[80%]">
                    <span className={`text-xs px-1 text-gray-400 ${msg.role === 'user' ? 'text-right' : ''}`}>{msg.role === 'user' ? 'You' : 'Saum AI'}</span>
                    <div className={`rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-gray-900 text-white rounded-tr-sm'
                        : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-2 justify-start">
                  <img src="/saumai Background Removed.png" alt="Saum AI" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
                  <div className="flex flex-col gap-1">
                    <span className="text-xs px-1 text-gray-400">Saum AI</span>
                    <div className="bg-gray-100 text-gray-500 rounded-2xl rounded-tl-sm px-4 py-3 text-sm italic">
                      Thinking...
                    </div>
                  </div>
                </div>
              )}

              {!isTyping && followUpSuggestions.length > 0 && (
                <div className="pt-1 space-y-2">
                  <p className="text-xs font-medium text-gray-400 px-1">Continue exploring:</p>
                  {followUpSuggestions.map(s => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all text-sm"
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Panel input */}
            <div className="px-4 pb-5 pt-3 border-t border-gray-200">
              <form onSubmit={handleSubmit} className="flex items-center gap-3">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={responseCount >= MAX_RESPONSES ? "Response limit reached" : "Ask me something"}
                  disabled={responseCount >= MAX_RESPONSES}
                  className="flex-1 bg-white text-gray-900 placeholder-gray-400 rounded-xl px-4 py-3 text-sm outline-none border border-gray-200 focus:border-gray-400 transition-colors disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={responseCount >= MAX_RESPONSES || !input.trim()}
                  className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-700 transition-colors disabled:opacity-30"
                >
                  <ArrowRight size={18} className="text-white" />
                </button>
              </form>
              <p className="text-xs text-center mt-3 text-gray-400">SaumAI can make mistakes. Check important info.</p>
            </div>
          </div>
        </>
      )}

      {!machineMode && <><section id="work-section" className={`w-full px-4 sm:px-8 md:px-12 pt-16 pb-10 transition-colors duration-300 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-[1200px] mx-auto">
          <h3 className={`text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-wide mb-6 sm:mb-8 text-center transition-colors duration-300 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Impact & Work
          </h3>

          <div className="space-y-6 md:space-y-8">
            {/* Skillz Store — full width top */}
            <div id="skillz-store-section" className={`backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 transition-all duration-300 ${
              theme === 'dark' ? 'bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-white/5' : 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200'
            }`}>
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="w-full md:w-1/2 flex justify-center">
                  <img src={theme === 'light' ? '/store-light.png' : '/bothimages.png'} alt="Store Revamp Before and After" className="w-full max-w-[900px] h-auto rounded-lg" />
                </div>
                <div className="w-full md:w-1/2 space-y-4">
                  <h4 className={`text-xl sm:text-2xl font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Skillz Store</h4>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>Revamped Store UI to boost user deposits through modern design.</p>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>Redesigned Skillz's deposit store to fix a critical problem - our store looked outdated and lacked excitement factor. Leading a complete UX overhaul with new engagement features helped drive $$ in incremental annual revenue.</p>
                  <Link to="/skillzstore" className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 group mt-3 ${
                    theme === 'dark' ? 'border border-white/20 hover:border-white/40 hover:bg-white/5 text-white/90 hover:text-white' : 'border border-gray-400 hover:border-gray-600 hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }`}>
                    <span className="text-sm font-medium">Read more</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <div className="pt-4 flex flex-wrap gap-2">
                    {[['Briefcase', 'Product Management'], ['Users', 'User Research'], ['BarChart3', 'A/B Testing'], ['TrendingUp', 'Competitive Analysis'], ['FileText', 'Product Documentation'], ['Palette', 'UX Design']].map(([icon, label]) => (
                      <span key={label} className={`px-3 py-1 text-sm rounded-full flex items-center gap-2 transition-colors duration-300 ${theme === 'dark' ? 'bg-white/10 text-white/80' : 'bg-gray-200 text-gray-700'}`}>
                        {icon === 'Briefcase' && <Briefcase size={14} />}
                        {icon === 'Users' && <Users size={14} />}
                        {icon === 'BarChart3' && <BarChart3 size={14} />}
                        {icon === 'TrendingUp' && <TrendingUp size={14} />}
                        {icon === 'FileText' && <FileText size={14} />}
                        {icon === 'Palette' && <Palette size={14} />}
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bundles */}
            <div id="bundles-section" className={`backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 transition-all duration-300 ${
              theme === 'dark' ? 'bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-white/5' : 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200'
            }`}>
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="w-full md:w-1/2 flex justify-center">
                  <img src={theme === 'light' ? '/bundles-light.png' : '/bundles.png'} alt="Bundles" className="w-full max-w-[900px] h-auto rounded-lg" />
                </div>
                <div className="w-full md:w-1/2 space-y-4">
                  <h4 className={`text-xl sm:text-2xl font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Skillz Bundled Offers</h4>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>Defined offer strategies to reduce incentives dependence & improve margins.</p>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>Led the strategy and configuration of Bundled Offers - combining real cash, bonus cash, Z's, and Tickets into multi-currency bundles. Reduced over-reliance on bonus cash and increased offer engagement through analytics modeling and A/B testing.</p>
                  <Link to="/bundles" className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 group mt-3 ${
                    theme === 'dark' ? 'border border-white/20 hover:border-white/40 hover:bg-white/5 text-white/90 hover:text-white' : 'border border-gray-400 hover:border-gray-600 hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }`}>
                    <span className="text-sm font-medium">Read more</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <div className="pt-4 flex flex-wrap gap-2">
                    {[['Briefcase', 'Product Management'], ['BarChart3', 'Analytics Modeling'], ['BarChart3', 'A/B Testing'], ['TrendingUp', 'Monetization Strategy'], ['Users', 'Competitive Analysis'], ['FileText', 'Product Documentation']].map(([icon, label]) => (
                      <span key={label} className={`px-3 py-1 text-sm rounded-full flex items-center gap-2 transition-colors duration-300 ${theme === 'dark' ? 'bg-white/10 text-white/80' : 'bg-gray-200 text-gray-700'}`}>
                        {icon === 'Briefcase' && <Briefcase size={14} />}
                        {icon === 'Users' && <Users size={14} />}
                        {icon === 'BarChart3' && <BarChart3 size={14} />}
                        {icon === 'TrendingUp' && <TrendingUp size={14} />}
                        {icon === 'FileText' && <FileText size={14} />}
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Verify Kicks */}
            <div id="verifykicks-section" className={`backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 transition-all duration-300 ${
              theme === 'dark' ? 'bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-white/5' : 'bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200'
            }`}>
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="w-full md:w-1/2 flex justify-center">
                  <img src="/new.png" alt="Mobile App Preview" className="w-full max-w-[500px] h-auto rounded-xl" />
                </div>
                <div className="w-full md:w-1/2 space-y-4">
                  <h4 className={`text-xl sm:text-2xl font-medium transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Verify Kicks</h4>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>AI-powered sneaker authentication for real buyers.</p>
                  <p className={`leading-relaxed transition-colors duration-300 ${theme === 'dark' ? 'text-white/70' : 'text-gray-600'}`}>I built Verify Kicks to solve a simple problem - people keep getting scammed by fake sneakers online. Using AI, our app authenticates shoes in seconds with just a few photos. It's fast, accurate, and designed for trust.</p>
                  <Link to="/verifykicks" className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 group mt-3 ${
                    theme === 'dark' ? 'border border-white/20 hover:border-white/40 hover:bg-white/5 text-white/90 hover:text-white' : 'border border-gray-400 hover:border-gray-600 hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }`}>
                    <span className="text-sm font-medium">Read more</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <div className="pt-4 flex flex-wrap gap-2">
                    {[['Briefcase', 'Product Management'], ['Users', 'User Research'], ['BarChart3', 'Product Strategy'], ['Palette', 'UX Design']].map(([icon, label]) => (
                      <span key={label} className={`px-3 py-1 text-sm rounded-full flex items-center gap-2 transition-colors duration-300 ${theme === 'dark' ? 'bg-white/10 text-white/80' : 'bg-gray-200 text-gray-700'}`}>
                        {icon === 'Briefcase' && <Briefcase size={14} />}
                        {icon === 'Users' && <Users size={14} />}
                        {icon === 'BarChart3' && <BarChart3 size={14} />}
                        {icon === 'Palette' && <Palette size={14} />}
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Side Projects */}
      <section id="side-projects-section" className={`w-full pt-16 pb-20 transition-colors duration-300 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 md:px-12">
          <h3 className={`text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-wide mb-2 text-center transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Weekend Experiments
          </h3>
          <p className={`text-center text-sm mb-8 transition-colors duration-300 ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>
            (AI projects built in &lt;48 hours)
          </p>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => { carouselPaused.current = true; }}
          onMouseLeave={() => { carouselPaused.current = false; }}
        >
          <div className="overflow-hidden">
            <div
              className="flex gap-5 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(calc(50% - ${sideProjectIndex * 356 + 168}px))` }}
            >
              {SIDE_PROJECTS.map((project, i) => (
                <div
                  key={i}
                  onClick={() => setSideProjectIndex(i)}
                  className={`flex-shrink-0 w-[336px] h-[525px] rounded-[2rem] overflow-hidden relative cursor-pointer transition-all duration-500 ${i === sideProjectIndex ? 'scale-100 opacity-100' : 'scale-90 opacity-90'}`}
                >
                  <div className={`absolute inset-0 ${project.bg}`} />
                  {project.image
                    ? <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                    : <div className="absolute inset-0 bg-black/45" />
                  }
                  {project.icon && i !== sideProjectIndex && (
                    <img src={project.icon} alt={`${project.title} icon`} className="absolute top-5 left-5 w-10 h-10 rounded-xl object-contain z-10" />
                  )}
                  {i !== sideProjectIndex && (
                    <div className="absolute inset-0 bg-black/80 flex flex-col justify-end p-6 gap-2">
                      <h4 className="text-white/80 font-bold text-xl">{project.title}</h4>
                      <div className="flex gap-2 flex-wrap">
                        {project.tags.map(tag => (
                          <span key={tag} className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-medium">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {i === sideProjectIndex && (
                    <div className="absolute inset-0 group/card">
                      {/* Base dark layer so text is always readable even on light images */}
                      <div className="absolute inset-0 bg-black/15" />
                      {/* Overlay darkens further on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover/card:from-black/92 group-hover/card:via-black/55 group-hover/card:to-black/20 transition-all duration-300" />
                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col justify-end p-6 pointer-events-none">
                        {!project.image && (
                          <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 group-hover/card:opacity-0">
                            <span className="text-xs tracking-widest uppercase text-white/50">screenshot</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 mb-2">
                          <h4 className="text-white font-bold text-xl">{project.title}</h4>
                          <ChevronRight size={18} className="text-white/70 transition-transform duration-300 group-hover/card:rotate-90 mt-0.5" />
                        </div>
                        <p className="text-white/70 text-xs leading-relaxed mb-3 max-h-0 opacity-0 group-hover/card:max-h-24 group-hover/card:opacity-100 overflow-hidden transition-all duration-300">{project.description}</p>
                        <div className="flex gap-2 flex-wrap">
                          {project.tags.map(tag => (
                            <span key={tag} className="px-3 py-1 rounded-full bg-white/15 text-white/80 text-xs font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center gap-2 mt-6">
          {SIDE_PROJECTS.map((_, i) => (
            <button
              key={i}
              onClick={() => setSideProjectIndex(i)}
              className={`rounded-full transition-all duration-300 ${i === sideProjectIndex ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
            />
          ))}
        </div>

      </section>

      {/* Travel Map */}
      <section className="w-full max-w-[1400px] mx-auto px-4 pt-16 pb-10">
        <div className="text-center mb-8">
          <h3 className={`text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-wide mb-6 sm:mb-8 transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Across the Map
          </h3>
          <div className="inline-flex rounded-full p-1 gap-1 bg-white/5 border border-white/10">
            <button
              onClick={() => setMapView('cities')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${mapView === 'cities' ? 'bg-white text-black' : 'text-white/50 hover:text-white/80'}`}
            >
              <MapPin size={11} />
              Cities
            </button>
            <button
              onClick={() => setMapView('parks')}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${mapView === 'parks' ? 'bg-white text-black' : 'text-white/50 hover:text-white/80'}`}
            >
              <Trees size={11} />
              National Parks
            </button>
          </div>
        </div>
        <div>
          <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 825, translate: [480, 260] }} width={960} height={520} style={{ width: '100%', height: 'auto' }}>
            <defs>
              <filter id="state-lift" x="-10%" y="-10%" width="120%" height="130%">
                <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.9" />
              </filter>
              <filter id="state-lift-hover" x="-10%" y="-10%" width="120%" height="130%">
                <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor="#000000" floodOpacity="1" />
              </filter>
            </defs>
            <Geographies geography={GEO_URL}>
              {({ geographies }) => (
                <>
                  {geographies.map(geo => {
                    const isHovered = hoveredGeo === geo.rsmKey;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isHovered ? '#252545' : '#16162a'}
                        stroke={isHovered ? 'rgba(147,197,253,0.5)' : 'rgba(255,255,255,0.08)'}
                        strokeWidth={isHovered ? 1 : 0.5}
                        style={{
                          outline: 'none',
                          cursor: 'default',
                          filter: isHovered ? 'url(#state-lift-hover)' : 'url(#state-lift)',
                          transition: 'fill 0.15s ease, filter 0.15s ease',
                        }}
                        onMouseEnter={() => setHoveredGeo(geo.rsmKey)}
                        onMouseLeave={() => setHoveredGeo(null)}
                      />
                    );
                  })}
                  <StateLabels geographies={geographies} />
                </>
              )}
            </Geographies>
            {mapView === 'cities' && FLIGHT_LINES.map((line, i) => (
              <Line
                key={i}
                from={line.from}
                to={line.to}
                stroke="rgba(147,197,253,0.45)"
                strokeWidth={1}
                strokeLinecap="round"
              />
            ))}
            {mapView === 'parks' && NATIONAL_PARKS.map(({ name, coordinates }) => {
              const isHovered = hoveredCity === name;
              return (
                <Marker key={name} coordinates={coordinates}>
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ fontSize: 12, cursor: 'pointer', pointerEvents: 'all' }}
                    onMouseEnter={() => setHoveredCity(name)}
                    onMouseLeave={() => setHoveredCity(null)}
                  >🌲</text>
                  {isHovered && (
                    <g pointerEvents="none">
                      <rect x={-28} y={-30} width={56} height={12} rx={3} fill="rgba(20,20,30,0.88)" />
                      <text textAnchor="middle" y={-22} style={{ fontFamily: 'sans-serif', fontSize: 7, fill: 'white', fontWeight: 600 }}>
                        {name}
                      </text>
                    </g>
                  )}
                </Marker>
              );
            })}
            {mapView === 'cities' && TRAVEL_CITIES.map(({ name, coordinates, icon }) => {
              const isHovered = hoveredCity === name;
              return (
                <Marker key={name} coordinates={coordinates}>
                  {icon === 'home' && (
                    <circle r={0} fill="none" stroke="#fbbf24" strokeWidth={1.5}>
                      <animate attributeName="r" from="5" to="14" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle
                    r={isHovered ? 5 : 3.5}
                    fill={icon === 'home' ? '#fbbf24' : 'white'}
                    fillOpacity={isHovered ? 1 : 0.85}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredCity(name)}
                    onMouseLeave={() => setHoveredCity(null)}
                  />
                  {isHovered && icon === 'home' && (
                    <g pointerEvents="none">
                      <rect x={-24} y={-35} width={48} height={24} rx={3} fill="rgba(20,20,30,0.88)" />
                      <text textAnchor="middle" style={{ fontFamily: 'sans-serif', fill: 'white' }}>
                        <tspan x={0} y={-25} style={{ fontSize: 7, fontWeight: 600 }}>Home</tspan>
                        <tspan x={0} dy={9} style={{ fontSize: 5.5, opacity: 0.65 }}>({name})</tspan>
                      </text>
                    </g>
                  )}
                  {isHovered && !icon && (
                    <g pointerEvents="none">
                      <rect x={-20} y={-21} width={40} height={12} rx={3} fill="rgba(20,20,30,0.88)" />
                      <text textAnchor="middle" y={-13} style={{ fontFamily: 'sans-serif', fontSize: 7, fill: 'white', fontWeight: 600 }}>{name}</text>
                    </g>
                  )}
                </Marker>
              );
            })}
          </ComposableMap>

        </div>
      </section>

      {/* Footer */}
      <footer className={`w-full py-3.5 transition-colors duration-300 ${theme === 'dark' ? 'bg-black border-t border-white/10' : 'bg-white border-t border-gray-200'}`}>
        <div className="max-w-[1200px] mx-auto px-4 flex justify-between items-center">
          <div className="flex-1" />
          <div className="flex flex-col items-center gap-1">
            <div className={`text-xs transition-colors duration-300 ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}>Nice to meet you!</div>
            <div className={`text-xs transition-colors duration-300 ${theme === 'dark' ? 'text-white/40' : 'text-gray-500'}`}>© {new Date().getFullYear()} Saumya Patni. All rights reserved</div>
          </div>
          <div className="flex-1 flex justify-end items-center gap-4">
            <a href="https://www.linkedin.com/in/saumya-patni/" target="_blank" rel="noopener noreferrer" className={`transition-colors ${theme === 'dark' ? 'text-white/40 hover:text-white/60' : 'text-gray-500 hover:text-gray-700'}`}>
              <Linkedin size={16} />
            </a>
            <a href="https://www.instagram.com/saumyapatni/" target="_blank" rel="noopener noreferrer" className={`transition-colors ${theme === 'dark' ? 'text-white/40 hover:text-white/60' : 'text-gray-500 hover:text-gray-700'}`}>
              {/* Instagram icon via SVG since lucide doesn't export Instagram in this version */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href="mailto:saum.patni@gmail.com" className={`transition-colors ${theme === 'dark' ? 'text-white/40 hover:text-white/60' : 'text-gray-500 hover:text-gray-700'}`}>
              <Mail size={16} />
            </a>
          </div>
        </div>
      </footer></>}

      {/* Memory game modal */}
      {showGame && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md" onClick={() => setShowGame(false)}>
          <div className="rounded-2xl overflow-hidden w-[360px] select-none" style={{ background: 'linear-gradient(145deg, #18181b, #09090b)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 25px 60px rgba(0,0,0,0.8)' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-600 uppercase mb-1">roBOTCHA™ — Access Protocol</div>
                <div className="text-white font-bold text-xl">Match all pairs to enter</div>
              </div>
              <button onClick={() => setShowGame(false)} className="text-zinc-500 hover:text-white transition-colors mt-1">
                <X size={18} />
              </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-4 gap-2.5 px-5 pb-5">
              {gameCards.map((card, index) => {
                const isFlipped = flipped.includes(index) || card.isMatched;
                const isMismatched = mismatch.includes(index);
                return (
                  <div key={card.id} className={`memory-card h-[72px] ${isMismatched ? 'shake' : ''} ${card.isMatched ? 'matched-pulse' : ''}`}>
                    <div className={`memory-card-inner ${isFlipped ? 'flipped' : ''}`}>
                      {/* Front (face down) */}
                      <button
                        onClick={() => handleCardClick(index)}
                        className="memory-card-front w-full h-full"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}
                        onMouseEnter={e => { if (!isFlipped) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                      >
                        <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.15)', fontFamily: 'monospace', fontWeight: 'bold' }}>?</span>
                      </button>
                      {/* Back (face up) */}
                      <div
                        className="memory-card-back w-full h-full text-2xl"
                        style={{
                          background: card.isMatched ? 'rgba(74,222,128,0.12)' : isMismatched ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${card.isMatched ? 'rgba(74,222,128,0.4)' : isMismatched ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.12)'}`,
                        }}
                      >
                        {card.emoji}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-5 pb-4 flex justify-between items-center border-t border-white/5 pt-3">
              <button onClick={initGame} className="text-[11px] font-mono text-zinc-600 hover:text-zinc-400 transition-colors tracking-wider">
                ↺ RESET
              </button>
              <span className="text-[10px] font-mono text-zinc-800">for machines, by machines</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
